"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import ArrowIcon from "@/components/ui/ArrowIcon";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";
import type { NavLink } from "@/components/layout/nav-links";
import { localePath, type Locale } from "@/lib/i18n/config";
import type { Dictionary } from "@/lib/i18n/getDictionary";

export default function MobileMenu({
  links,
  dict,
  locale,
}: {
  links: NavLink[];
  dict: Dictionary;
  locale: Locale;
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className="lg:hidden">
      <button
        type="button"
        aria-label={dict.nav.openMenu}
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink"
      >
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-white">
          <div className="flex items-center justify-between px-6 py-3">
            <Link href={localePath(locale, "/")} onClick={close} className="flex items-center">
              <Image
                src="/images/logo-valorhel.png"
                alt="Valorhel Consulting"
                width={121}
                height={70}
                className="h-12 w-auto"
              />
            </Link>
            <button
              type="button"
              aria-label={dict.nav.closeMenu}
              onClick={close}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink"
            >
              <svg
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="m6 6 12 12M18 6 6 18" />
              </svg>
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-6 py-6">
            <ul className="space-y-1">
              {links.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    onClick={close}
                    className="block rounded-xl px-4 py-3 text-lg font-medium text-ink transition hover:bg-paper"
                  >
                    {link.label}
                  </Link>
                  {link.children && (
                    <ul className="ml-4 border-l border-ink/10 pl-2">
                      {link.children.map((child) => (
                        <li key={child.href}>
                          <Link
                            href={child.href}
                            onClick={close}
                            className="block rounded-xl px-4 py-2.5 text-base text-ink/70 transition hover:bg-paper hover:text-ink"
                          >
                            {child.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className="border-t border-ink/10 px-6 py-5">
            <div className="flex items-center justify-between gap-4">
              <LanguageSwitcher locale={locale} />

              <Link
                href={localePath(locale, "/contact")}
                onClick={close}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-ink-deep px-5 py-3 text-sm font-medium text-white transition hover:bg-ink"
              >
                {dict.nav.contact}
                <ArrowIcon className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
